import { useMemo, useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

import { usePositionsGrouped } from '../api/usePositions';
import {
  useUnwindingRules,
  usePanic,
  useGlobalToggle,
  useTogglePlatform,
  useCreateRule,
  useSetSimParams,
  useOrders
} from '../api/useUnwinding';
import { OrdersPanel } from '../components/unwinding/OrdersPanel';
import { PredictionPosition, PositionsGroupedByPair } from '../types';
import { UnwindingRule, SimulationResult } from '../types/unwinding.types';
import { useUnwindingSocket } from '../hooks/useUnwindingSocket';

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/** Format price with 3 decimal places (matching platform precision).
 *  0.958 → "0.958", 0.95 → "0.950", 0.07 → "0.070", 0.016 → "0.016", 0.0046 → "0.0046" */
const fmtPrice = (v: number | undefined | null): string => {
  if (v == null || isNaN(v)) return '\u2014';
  if (v === 0) return '0.000';
  if (v >= 0.01) return v.toFixed(3);      // 0.958, 0.950, 0.070
  return v.toFixed(4);                      // 0.0046
};

const fmtQty = (v: number | undefined | null): string => {
  if (v == null || isNaN(v)) return '\u2014';
  if (v <= 0) return '\u2014';
  if (v >= 1_000_000) return `${Math.floor(v / 100_000) / 10}M`;
  if (v >= 1_000) return `${Math.floor(v / 100) / 10}k`;
  if (v >= 100) return String(Math.floor(v));
  return String(v);
};



/** Format position contracts: integer count, no commas (contracts are whole units) */
const fmtContracts = (v: number | undefined | null): string => {
  if (v == null || v === 0) return '\u2014';
  return Math.round(v).toString();
};

/** Get max position size for sorting */
const getMaxPositionSize = (group: PositionsGroupedByPair): number => {
  const poly = group.positions.find(p => p.platform === 'polymarket' && p.status === 'open');
  const kalshi = group.positions.find(p => p.platform === 'kalshi' && p.status === 'open');
  return Math.max(poly?.contracts ?? 0, kalshi?.contracts ?? 0);
};

const hasAnyContracts = (group: PositionsGroupedByPair): boolean => {
  const poly = group.positions.find(p => p.platform === 'polymarket' && p.status === 'open');
  const kalshi = group.positions.find(p => p.platform === 'kalshi' && p.status === 'open');
  return (poly?.contracts ?? 0) > 0 || (kalshi?.contracts ?? 0) > 0;
};

const hSx = {
  fontSize: '0.58rem', fontWeight: 600, color: 'grey.500',
  py: 0.2, px: 0.2, textTransform: 'uppercase' as const,
  letterSpacing: '0.02em', whiteSpace: 'nowrap' as const,
  borderBottom: '1px solid rgba(255,255,255,0.06)'
};
const gSx = { ...hSx, textAlign: 'center' as const, borderBottom: '1px solid rgba(255,255,255,0.1)' };
const cSx = { fontSize: '0.72rem', py: 0.15, px: 0.15 } as const;

const findPos = (group: PositionsGroupedByPair, platform: 'polymarket' | 'kalshi'): PredictionPosition | undefined =>
  group.positions.find(p => p.platform === platform && p.status === 'open');

const findRule = (rules: UnwindingRule[] | undefined, pairId: string | null): UnwindingRule | undefined =>
  pairId ? rules?.find(r => r.pairId === pairId) : undefined;

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export const UnwindingPage = () => {
  const { data: positionsData, isLoading } = usePositionsGrouped({ status: 'open' });
  const { data: rules } = useUnwindingRules();
  const panicMutation = usePanic();
  const globalToggleMutation = useGlobalToggle();
  const togglePlatformMutation = useTogglePlatform();
  const createRuleMutation = useCreateRule();
  const simParamsMutation = useSetSimParams();
  const { isConnected, getState, injectStates, orders: wsOrders } = useUnwindingSocket();
  const { data: ordersData } = useOrders();
  const ordersToShow = wsOrders ?? ordersData;
  const [expandedPairId, setExpandedPairId] = useState<string | null>(null);

  const globalEnabled = Array.isArray(rules) && rules.some(r => r.status === 'active');


  // Sort by max position size descending
  const groups = useMemo(() => {
    if (!positionsData?.groups) return [];
    return positionsData.groups
      .filter(g => (findPos(g, 'polymarket') || findPos(g, 'kalshi')) && hasAnyContracts(g))
      .sort((a, b) => getMaxPositionSize(b) - getMaxPositionSize(a));
  }, [positionsData]);

  return (
    <Box sx={{ px: 2, pt: 1, height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      {/* Title bar */}
      <Stack direction="row" alignItems="center" sx={{ mb: 0.5, flexShrink: 0 }}>
        <Typography variant="h5" sx={{ color: 'white' }}>Position Unwinding</Typography>
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            icon={<FiberManualRecordIcon sx={{ fontSize: '0.6rem !important' }} />}
            label={isConnected ? 'Live' : 'Disconnected'}
            size="small" color={isConnected ? 'success' : 'default'} variant="outlined"
            sx={{ fontSize: '0.7rem' }}
          />
          <FormControlLabel
            control={<Switch size="small" checked={globalEnabled} onChange={(_, v) => globalToggleMutation.mutate(v)} />}
            label="Global"
            sx={{ '& .MuiTypography-root': { fontSize: '0.75rem' }, mr: 0 }}
          />
          <Button
            variant="contained" color="error" size="small"
            startIcon={<WarningAmberIcon />}
            onClick={() => { if (window.confirm('PANIC: Cancel ALL orders and stop everything?')) panicMutation.mutate(); }}
            sx={{ fontSize: '0.7rem' }}
            disabled={panicMutation.isLoading}
          >{panicMutation.isLoading ? <CircularProgress size={14} color="inherit" /> : 'PANIC'}</Button>
        </Stack>
      </Stack>

      {/* Positions table — scrollable with sticky headers */}
      <Box sx={{ flex: 3, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : groups.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>No open positions found.</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...gSx, width: 170, top: 0, backgroundColor: '#1a1a2e' }} rowSpan={2}>Market</TableCell>
                  <TableCell sx={{ ...gSx, width: 45, top: 0, backgroundColor: '#1a1a2e' }} rowSpan={2}>Pos</TableCell>
                  <TableCell sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }} colSpan={8}>Market Data</TableCell>
                  <TableCell sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }} colSpan={3}>Parameters</TableCell>
                  <TableCell sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }} colSpan={4}>Simulation</TableCell>
                  <TableCell sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }} colSpan={2}>Ctrl</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ ...hSx, width: 24, top: 22, backgroundColor: '#1a1a2e' }} align="right">Qty</TableCell>
                  <TableCell sx={{ ...hSx, width: 32, top: 22, backgroundColor: '#1a1a2e' }} align="right">P.Bid</TableCell>
                  <TableCell sx={{ ...hSx, width: 32, top: 22, backgroundColor: '#1a1a2e' }} align="right">P.Ask</TableCell>
                  <TableCell sx={{ ...hSx, width: 24, top: 22, backgroundColor: '#1a1a2e' }} align="right">Qty</TableCell>
                  <TableCell sx={{ ...hSx, width: 24, top: 22, backgroundColor: '#1a1a2e' }} align="right">Qty</TableCell>
                  <TableCell sx={{ ...hSx, width: 32, top: 22, backgroundColor: '#1a1a2e' }} align="right">K.Bid</TableCell>
                  <TableCell sx={{ ...hSx, width: 32, top: 22, backgroundColor: '#1a1a2e' }} align="right">K.Ask</TableCell>
                  <TableCell sx={{ ...hSx, width: 24, top: 22, backgroundColor: '#1a1a2e' }} align="right">Qty</TableCell>
                  <TableCell sx={{ ...hSx, width: 32, top: 22, backgroundColor: '#1a1a2e' }} align="right">TNA</TableCell>
                  <TableCell sx={{ ...hSx, width: 30, top: 22, backgroundColor: '#1a1a2e' }} align="right">Size</TableCell>
                  <TableCell sx={{ ...hSx, width: 36, top: 22, backgroundColor: '#1a1a2e' }} align="right">MaxV</TableCell>
                  <TableCell sx={{ ...hSx, width: 32, top: 22, backgroundColor: '#1a1a2e' }} align="right">K.Bid</TableCell>
                  <TableCell sx={{ ...hSx, width: 32, top: 22, backgroundColor: '#1a1a2e' }} align="right">P.Ask</TableCell>
                  <TableCell sx={{ ...hSx, width: 32, top: 22, backgroundColor: '#1a1a2e' }} align="right">P.Bid</TableCell>
                  <TableCell sx={{ ...hSx, width: 32, top: 22, backgroundColor: '#1a1a2e' }} align="right">K.Ask</TableCell>
                  <TableCell sx={{ ...hSx, width: 28, top: 22, backgroundColor: '#1a1a2e' }} align="center">P</TableCell>
                  <TableCell sx={{ ...hSx, width: 28, top: 22, backgroundColor: '#1a1a2e' }} align="center">K</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map(group => {
                  const rule = findRule(rules, group._id);
                  // Always look up by pairId first (backend uses pairId for position-only states),
                  // then fall back to rule._id for rule-based states
                  const liveState = (group._id ? getState(group._id) : undefined)
                    ?? (rule ? getState(rule._id) : undefined);
                  return (
                    <PositionRow
                      key={group._id ?? group.pairName}
                      group={group}
                      polyPos={findPos(group, 'polymarket')}
                      kalshiPos={findPos(group, 'kalshi')}
                      rule={rule}
                      liveState={liveState}
                      isExpanded={expandedPairId === (group._id ?? group.pairName)}
                      onToggleExpand={() => setExpandedPairId(prev => prev === (group._id ?? group.pairName) ? null : (group._id ?? group.pairName))}
                      onTogglePlatform={(platform, enabled) => {
                        if (rule) {
                          togglePlatformMutation.mutate({ id: rule._id, platform, enabled });
                        } else if (enabled && group._id) {
                          // Create rule with defaults when toggling ON without a rule
                          const pp = findPos(group, 'polymarket');
                          const kp = findPos(group, 'kalshi');
                          createRuleMutation.mutate({
                            pairId: group._id,
                            pairName: group.pairName,
                            expiryDate: group.expiryDate || new Date().toISOString(),
                            kalshiConfig: kp ? { side: kp.side, ticker: kp.marketId, contractsToSell: 50, targetTnaPercent: 5 } : undefined,
                            polymarketConfig: pp ? { side: pp.side, tokenId: pp.marketId, conditionId: pp.marketId, contractsToSell: 50, targetTnaPercent: 5 } : undefined,
                            maxVolumeUsd: 500,
                            volumePeriod: 'day',
                            kalshiEnabled: platform === 'kalshi',
                            polymarketEnabled: platform === 'polymarket',
                          });
                        }
                      }}
                      onSaveParams={async (params) => {
                        if (!group._id) return false;
                        try {
                          const data: any = await simParamsMutation.mutateAsync({
                            pairId: group._id,
                            tna: params.tna,
                            size: params.size,
                            maxVol: params.maxVol,
                          });
                          console.log('[SimParams] Response:', data?.state ? 'has state' : 'no state', data?.state?.simulation ? 'has sim' : 'no sim');
                          if (Array.isArray(data?.states) && data.states.length > 0) {
                            injectStates(data.states);
                          } else if (data?.state) {
                            injectStates([data.state]);
                          }
                          return true;
                        } catch {
                          return false;
                        }
                      }}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Orders panel — scrollable */}
      <Box sx={{ flex: 2, overflow: 'auto', minHeight: 0, borderTop: '1px solid rgba(255,255,255,0.08)', mt: 0.5 }}>
        <OrdersPanel data={ordersToShow} positions={positionsData?.groups} />
      </Box>
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/* Simulation Breakdown Panel                                          */
/* ------------------------------------------------------------------ */

const stepLabel = { fontSize: '0.6rem', color: 'grey.500' } as const;
const stepValue = { fontSize: '0.62rem', fontFamily: 'monospace' } as const;

const SimulationBreakdownPanel = ({ polyAsMaker, kalshiAsMaker, expiryDate, daysToExpiry }: {
  polyAsMaker: SimulationResult | null;
  kalshiAsMaker: SimulationResult | null;
  expiryDate?: string | Date | null;
  daysToExpiry?: number | null;
}) => {
  const renderDirection = (label: string, result: SimulationResult | null) => {
    if (!result?.breakdown) return (
      <Box sx={{ flex: 1, p: 1 }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, mb: 0.5 }}>{label}</Typography>
        <Typography sx={{ fontSize: '0.65rem', color: 'grey.500' }}>No data</Typography>
      </Box>
    );
    const b = result.breakdown;
    return (
      <Box sx={{ flex: 1, p: 1 }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, mb: 0.5, color: '#42a5f5' }}>{label}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 8px' }}>
          <Typography sx={stepLabel}>1. Raw taker bid</Typography>
          <Typography sx={stepValue}>{b.rawTakerBid.toFixed(4)}</Typography>

          <Typography sx={stepLabel}>2. Taker fee ({b.takerPlatform}, {(b.takerFeeRate * 100).toFixed(2)}%)</Typography>
          <Typography sx={stepValue}>-{b.takerFeeAmount.toFixed(4)}</Typography>

          <Typography sx={stepLabel}>3. Net taker bid</Typography>
          <Typography sx={stepValue}>{b.netTakerBid.toFixed(4)}</Typography>

          <Typography sx={stepLabel}>4. Min sell price (TNA={b.targetTna}%, {b.effectiveDays.toFixed(1)}d)</Typography>
          <Typography sx={stepValue}>{b.minSellPrice.toFixed(4)}</Typography>

          <Typography sx={stepLabel}>5. Best ask on maker book</Typography>
          <Typography sx={stepValue}>{b.makerAskTopPrice.toFixed(4)} ({Math.round(b.makerAskTopSize)} lots)</Typography>

          <Typography sx={stepLabel}>6. Maker price (at min sell price)</Typography>
          <Typography sx={{ ...stepValue, fontWeight: 600, color: '#42a5f5' }}>{b.makerPrice.toFixed(4)}</Typography>

          <Typography sx={stepLabel}>7. Maker fee ({(b.makerFeeRate * 100).toFixed(2)}%)</Typography>
          <Typography sx={stepValue}>-{b.makerFeeAmount.toFixed(4)}</Typography>

          <Typography sx={stepLabel}>8. Net maker proceeds</Typography>
          <Typography sx={stepValue}>{b.netMakerPrice.toFixed(4)}</Typography>

          <Typography sx={stepLabel}>9. Size (req={b.requestedSize}, taker={Math.round(b.takerLiquidityCap)}, pos={b.positionCap || '\u221e'})</Typography>
          <Typography sx={stepValue}>{b.effectiveSize} contracts</Typography>

          <Typography sx={stepLabel}>10. Achieved TNA</Typography>
          <Typography sx={{ ...stepValue, fontWeight: 600, color: b.achievedTna >= 0 ? '#4caf50' : '#f44336' }}>
            {b.achievedTna.toFixed(2)}%
          </Typography>
        </Box>
      </Box>
    );
  };

  const expiryStr = expiryDate ? new Date(expiryDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'unknown';
  const daysStr = daysToExpiry != null ? `${daysToExpiry.toFixed(1)} days` : 'unknown';

  return (
    <Box sx={{ py: 1, px: 2, backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: 1 }}>
      {/* Expiry header */}
      <Box sx={{ display: 'flex', gap: 3, mb: 1, pb: 0.5, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <Typography sx={{ fontSize: '0.65rem', color: 'grey.400' }}>
          <b>Expiry:</b> {expiryStr}
        </Typography>
        <Typography sx={{ fontSize: '0.65rem', color: daysToExpiry != null && daysToExpiry < 1 ? '#f44336' : 'grey.400' }}>
          <b>Days to expiry:</b> {daysStr}
        </Typography>
        <Typography sx={{ fontSize: '0.65rem', color: 'grey.400' }}>
          <b>Effective days:</b> {polyAsMaker?.breakdown?.effectiveDays?.toFixed(1) ?? kalshiAsMaker?.breakdown?.effectiveDays?.toFixed(1) ?? 'N/A'}
        </Typography>
      </Box>
      {/* Two-column breakdown */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {renderDirection('Polymarket as Maker', polyAsMaker)}
        <Box sx={{ width: '1px', backgroundColor: 'rgba(255,255,255,0.08)' }} />
        {renderDirection('Kalshi as Maker', kalshiAsMaker)}
      </Box>
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/* Row                                                                 */
/* ------------------------------------------------------------------ */

interface PositionRowProps {
  group: PositionsGroupedByPair;
  polyPos?: PredictionPosition;
  kalshiPos?: PredictionPosition;
  rule?: UnwindingRule;
  liveState?: ReturnType<ReturnType<typeof useUnwindingSocket>['getState']>;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onTogglePlatform: (platform: 'kalshi' | 'polymarket', enabled: boolean) => void;
  onSaveParams: (params: { tna: number; size: number; maxVol?: number }) => Promise<boolean>;
}

const PositionRow = ({ group, polyPos, kalshiPos, rule, liveState, isExpanded, onToggleExpand, onTogglePlatform, onSaveParams }: PositionRowProps) => {
  // Init from: liveState simParams (WS) > rule config (REST) > defaults
  const initTna = liveState?.simTna?.toString()
    ?? rule?.kalshiConfig?.targetTnaPercent?.toString()
    ?? rule?.polymarketConfig?.targetTnaPercent?.toString()
    ?? '';
  const initSize = liveState?.simSize?.toString()
    ?? rule?.kalshiConfig?.contractsToSell?.toString()
    ?? rule?.polymarketConfig?.contractsToSell?.toString()
    ?? '50';
  const initMaxVol = liveState?.simMaxVolume?.toString()
    ?? rule?.maxVolumeUsd?.toString()
    ?? '500';

  const [tna, setTna] = useState(initTna);
  const [size, setSize] = useState(initSize);
  const [maxVol, setMaxVol] = useState(initMaxVol);
  const [savedTna, setSavedTna] = useState(initTna);
  const [savedSize, setSavedSize] = useState(initSize);
  const [savedMaxVol, setSavedMaxVol] = useState(initMaxVol);

  // Sync inputs when WS data arrives with different simParams
  useEffect(() => {
    if (liveState?.simTna != null) {
      const wsTna = liveState.simTna.toString();
      if (wsTna !== savedTna && wsTna !== tna) {
        setTna(wsTna);
        setSavedTna(wsTna);
      }
    }
    if (liveState?.simSize != null) {
      const wsSize = liveState.simSize.toString();
      if (wsSize !== savedSize && wsSize !== size) {
        setSize(wsSize);
        setSavedSize(wsSize);
      }
    }
    if (liveState?.simMaxVolume != null) {
      const wsMaxVol = liveState.simMaxVolume.toString();
      if (wsMaxVol !== savedMaxVol && wsMaxVol !== maxVol) {
        setMaxVol(wsMaxVol);
        setSavedMaxVol(wsMaxVol);
      }
    }
  }, [liveState?.simTna, liveState?.simSize, liveState?.simMaxVolume]);
  const [saving, setSaving] = useState(false);
  const [togglingPoly, setTogglingPoly] = useState(false);
  const [togglingKalshi, setTogglingKalshi] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{ platform: 'kalshi' | 'polymarket'; enabled: boolean } | null>(null);

  const isDirty = tna !== savedTna || size !== savedSize || maxVol !== savedMaxVol;

  const handleConfirmParams = useCallback(async () => {
    if (!isDirty) return;
    const parsedTna = parseFloat(tna);
    const parsedSize = parseInt(size);
    const parsedMaxVol = parseFloat(maxVol);
    const safeTna = Number.isFinite(parsedTna) ? parsedTna : 5;
    const safeSize = Number.isFinite(parsedSize) ? parsedSize : 50;
    const safeMaxVol = Number.isFinite(parsedMaxVol) ? parsedMaxVol : undefined;

    setSaving(true);
    const ok = await onSaveParams({
      tna: safeTna,
      size: safeSize,
      maxVol: safeMaxVol
    });
    if (ok) {
      setSavedTna(tna);
      setSavedSize(size);
      setSavedMaxVol(maxVol);
    }
    setSaving(false);
  }, [tna, size, maxVol, onSaveParams, isDirty]);

  const doToggle = useCallback((platform: 'kalshi' | 'polymarket', enabled: boolean) => {
    const setLoading = platform === 'kalshi' ? setTogglingKalshi : setTogglingPoly;
    setLoading(true);
    onTogglePlatform(platform, enabled);
    setTimeout(() => setLoading(false), 2000);
  }, [onTogglePlatform]);

  const handleToggle = useCallback((platform: 'kalshi' | 'polymarket', enabled: boolean) => {
    if (enabled) {
      setConfirmDialog({ platform, enabled });
    } else {
      doToggle(platform, false);
    }
  }, [doToggle]);

  const handleConfirmToggle = useCallback(() => {
    if (confirmDialog) {
      doToggle(confirmDialog.platform, confirmDialog.enabled);
      setConfirmDialog(null);
    }
  }, [confirmDialog, doToggle]);

  const pBid = liveState?.polyBid; const pAsk = liveState?.polyAsk;
  const kBid = liveState?.kalshiBid; const kAsk = liveState?.kalshiAsk;
  const sim = liveState?.simulation;

  const inSx = {
    '& .MuiInputBase-input': { fontSize: '0.62rem', py: 0.15, px: 0.2, textAlign: 'right', width: 28 },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.08)' },
    minWidth: 0,
  };

  const tooltipTitle = (
    <Box>
      <Typography sx={{ fontSize: '0.7rem' }}>{group.polymarketTitle || group.pairName}</Typography>
      <Typography sx={{ fontSize: '0.65rem', color: 'grey.400' }}>{group.kalshiTitle || kalshiPos?.marketId || group.pairName}</Typography>
    </Box>
  );

  return (
    <>
    <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' }, cursor: 'pointer' }} onClick={onToggleExpand}>
      {/* Market */}
      <TableCell sx={{ ...cSx, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.2 }}>
          {isExpanded ? <KeyboardArrowDownIcon sx={{ fontSize: 14, mt: 0.2, color: 'grey.500' }} /> : <KeyboardArrowRightIcon sx={{ fontSize: 14, mt: 0.2, color: 'grey.500' }} />}
        <Tooltip title={tooltipTitle} placement="top-start">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.15, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <a href={polyPos?.marketUrl ?? liveState?.polymarketMarketUrl ?? undefined} target="_blank" rel="noopener" onClick={e => e.stopPropagation()}>
                <img src="/assets/exchanges/png/100x100/polymarket.png" alt="P" style={{ width: 13, height: 13, borderRadius: '50%' }} />
              </a>
              <Typography sx={{ fontSize: '0.65rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                {group.polymarketTitle || group.pairName}
              </Typography>
              {polyPos && <Chip label={polyPos.side.toUpperCase()} size="small" color={polyPos.side === 'yes' ? 'success' : 'error'} variant="outlined" sx={{ fontSize: '0.42rem', height: 12, '& .MuiChip-label': { px: 0.25 }, opacity: 0.7 }} />}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <a href={kalshiPos?.marketUrl ?? liveState?.kalshiMarketUrl ?? undefined} target="_blank" rel="noopener" onClick={e => e.stopPropagation()}>
                <img src="/assets/exchanges/png/100x100/kalshi.png" alt="K" style={{ width: 13, height: 13, borderRadius: '50%' }} />
              </a>
              <Typography sx={{ fontSize: '0.6rem', color: 'grey.400', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                {group.kalshiTitle || kalshiPos?.marketId || group.pairName}
              </Typography>
              {kalshiPos && <Chip label={kalshiPos.side.toUpperCase()} size="small" color={kalshiPos.side === 'yes' ? 'success' : 'error'} variant="outlined" sx={{ fontSize: '0.42rem', height: 12, '& .MuiChip-label': { px: 0.25 }, opacity: 0.7 }} />}
            </Box>
          </Box>
        </Tooltip>
        </Box>
      </TableCell>

      {/* Position size — both platforms stacked, right-aligned */}
      <TableCell sx={{ ...cSx, verticalAlign: 'top' }} align="right">
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, lineHeight: 1.4 }}>{fmtContracts(polyPos?.contracts)}</Typography>
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, lineHeight: 1.4 }}>{fmtContracts(kalshiPos?.contracts)}</Typography>
      </TableCell>

      {/* Market Data */}
      <TableCell sx={cSx} align="right"><Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>{fmtQty(pBid?.size)}</Typography></TableCell>
      <TableCell sx={cSx} align="right"><Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{fmtPrice(pBid?.price ?? polyPos?.topBid)}</Typography></TableCell>
      <TableCell sx={cSx} align="right"><Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{fmtPrice(pAsk?.price ?? polyPos?.topAsk)}</Typography></TableCell>
      <TableCell sx={cSx} align="right"><Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>{fmtQty(pAsk?.size)}</Typography></TableCell>
      <TableCell sx={cSx} align="right"><Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>{fmtQty(kBid?.size)}</Typography></TableCell>
      <TableCell sx={cSx} align="right"><Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{fmtPrice(kBid?.price ?? kalshiPos?.topBid)}</Typography></TableCell>
      <TableCell sx={cSx} align="right"><Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{fmtPrice(kAsk?.price ?? kalshiPos?.topAsk)}</Typography></TableCell>
      <TableCell sx={cSx} align="right"><Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>{fmtQty(kAsk?.size)}</Typography></TableCell>

      {/* Parameters (editable) + confirm button */}
      <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
        <TextField variant="outlined" size="small" value={tna} placeholder="5" onChange={e => setTna(e.target.value)} onBlur={handleConfirmParams} onKeyDown={e => { if (e.key === 'Enter') handleConfirmParams(); }} sx={inSx} />
      </TableCell>
      <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
        <TextField variant="outlined" size="small" value={size} placeholder="50" onChange={e => setSize(e.target.value)} onBlur={handleConfirmParams} onKeyDown={e => { if (e.key === 'Enter') handleConfirmParams(); }} sx={inSx} />
      </TableCell>
      <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
        <Stack direction="row" alignItems="center" spacing={0} justifyContent="flex-end">
          <TextField variant="outlined" size="small" value={maxVol} placeholder="500" onChange={e => setMaxVol(e.target.value)} onBlur={handleConfirmParams} onKeyDown={e => { if (e.key === 'Enter') handleConfirmParams(); }} sx={inSx} />
          {saving ? (
            <CircularProgress size={12} sx={{ ml: 0.2 }} />
          ) : isDirty ? (
            <IconButton size="small" onClick={handleConfirmParams} sx={{ p: 0.1, ml: 0.2, color: 'success.main' }}>
              <CheckIcon sx={{ fontSize: '0.8rem' }} />
            </IconButton>
          ) : null}
        </Stack>
      </TableCell>

      {/* Simulation — dim while recalculating, spinner in first cell */}
      <TableCell sx={cSx} align="right">
        {saving
          ? <CircularProgress size={10} sx={{ color: 'grey.500' }} />
          : <Typography sx={{ fontSize: '0.7rem', color: '#ff9800' }}>{fmtPrice(sim?.polyAsMaker?.takerPrice)}</Typography>}
      </TableCell>
      <TableCell sx={cSx} align="right"><Typography sx={{ fontSize: '0.7rem', color: '#42a5f5', fontWeight: 600, opacity: saving ? 0.3 : 1 }}>{fmtPrice(sim?.polyAsMaker?.makerPrice)}</Typography></TableCell>
      <TableCell sx={cSx} align="right"><Typography sx={{ fontSize: '0.7rem', color: '#ff9800', opacity: saving ? 0.3 : 1 }}>{fmtPrice(sim?.kalshiAsMaker?.takerPrice)}</Typography></TableCell>
      <TableCell sx={cSx} align="right"><Typography sx={{ fontSize: '0.7rem', color: '#42a5f5', fontWeight: 600, opacity: saving ? 0.3 : 1 }}>{fmtPrice(sim?.kalshiAsMaker?.makerPrice)}</Typography></TableCell>

      {/* Controls */}
      <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
        {togglingPoly ? <CircularProgress size={14} /> : (
          <Switch size="small" checked={rule ? rule.polymarketEnabled !== false : false} onChange={(_, v) => handleToggle('polymarket', v)} />
        )}
      </TableCell>
      <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
        {togglingKalshi ? <CircularProgress size={14} /> : (
          <Switch size="small" checked={rule ? rule.kalshiEnabled !== false : false} onChange={(_, v) => handleToggle('kalshi', v)} />
        )}
      </TableCell>

    </TableRow>

    {/* Expanded simulation breakdown */}
    {isExpanded && sim && (
      <TableRow>
        <TableCell colSpan={19} sx={{ py: 0, px: 1, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <SimulationBreakdownPanel
            polyAsMaker={sim.polyAsMaker}
            kalshiAsMaker={sim.kalshiAsMaker}
            expiryDate={liveState?.expiryDate ?? group.expiryDate}
            daysToExpiry={liveState?.daysToExpiry ?? group.daysToExpiry}
          />
        </TableCell>
      </TableRow>
    )}

    {/* Confirmation dialog */}
    <Dialog open={!!confirmDialog} onClose={() => setConfirmDialog(null)} maxWidth="xs">
      <DialogTitle sx={{ fontSize: '0.9rem', pb: 0.5 }}>
        Activate {confirmDialog?.platform === 'kalshi' ? 'Kalshi' : 'Polymarket'} Maker
      </DialogTitle>
      <DialogContent>
        <Box sx={{ fontSize: '0.8rem', display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
          <Typography sx={{ fontSize: '0.8rem' }}><b>Market:</b> {group.pairName}</Typography>
          <Typography sx={{ fontSize: '0.8rem' }}><b>Platform:</b> {confirmDialog?.platform === 'kalshi' ? 'Kalshi' : 'Polymarket'} (as maker)</Typography>
          <Typography sx={{ fontSize: '0.8rem' }}><b>Target TNA:</b> {tna || '5'}%</Typography>
          <Typography sx={{ fontSize: '0.8rem' }}><b>Order size:</b> {size || '50'} contracts</Typography>
          <Typography sx={{ fontSize: '0.8rem' }}><b>Max volume:</b> ${maxVol || '500'}/day</Typography>
          {confirmDialog?.platform === 'kalshi' && kalshiPos && (
            <Typography sx={{ fontSize: '0.8rem' }}><b>Position:</b> {kalshiPos.side.toUpperCase()} {kalshiPos.contracts} @ {kalshiPos.avgPrice.toFixed(2)}</Typography>
          )}
          {confirmDialog?.platform === 'polymarket' && polyPos && (
            <Typography sx={{ fontSize: '0.8rem' }}><b>Position:</b> {polyPos.side.toUpperCase()} {polyPos.contracts} @ {polyPos.avgPrice.toFixed(2)}</Typography>
          )}
          <Typography sx={{ fontSize: '0.75rem', color: 'warning.main', mt: 0.5 }}>
            This will start placing real orders on {confirmDialog?.platform === 'kalshi' ? 'Kalshi' : 'Polymarket'}.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmDialog(null)} size="small">Cancel</Button>
        <Button onClick={handleConfirmToggle} variant="contained" color="success" size="small">Activate</Button>
      </DialogActions>
    </Dialog>
    </>
  );
};
